import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { inventoryAPI, suppliersAPI, contractsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, Save } from 'lucide-react';
import { toast } from 'sonner';
import { showApiError } from '@/lib/errorHandler';
import { SearchableSelect } from '@/components/SearchableSelect';

const ITEM_TYPES = ['Fixed Asset', 'Raw Material', 'Service', 'Consumable', 'Semi-Finished Part', 'Finished Good', 'Scrap', 'Job Work Item', 'Sheet Metal Child Part'];
const UOMS = ['Nos', 'Kg', 'Grams', 'Meters', 'mm', 'Liters', 'Sets', 'Pairs', 'Rolls', 'Sheets', 'Feet', 'Inches', 'Sq.m', 'Sq.ft', 'Cu.m'];
const SOURCING_OPTIONS = [
  { value: 'buy', label: 'Buy' },
  { value: 'make', label: 'Make' },
  { value: 'receive_from_customer', label: 'Receive from Customer' },
];

export default function ItemFormPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const copyFromId = searchParams.get('copyFrom');

  const [form, setForm] = useState({
    item_name: '', item_type: 'Raw Material', unique_id: '', external_id: '',
    description: '', drawing_number: '', revision: '', unit_of_measure: 'Nos',
    secondary_uom: '', conversion_factor: '',
    is_inventory_item: true, make_or_buy: 'buy', preferred_supplier_id: '',
    preferred_supplier_name: '', category: '', min_stock_level: 0, reorder_level: 0,
    hsn_code: '', material_spec: '', is_cutting_tool: false, max_regrinds: 2,
    customer_id: '', customer_name: '',
    auto_indent: false, perform_qc_on_receipt: false, calibration_required: false,
    auto_notify_low_stock: false, default_godown_id: '',
    sub_category: '', sheet_grade: '', sheet_thickness: '', sheet_length: '', sheet_width: '',
    unit_weight_value: '', unit_weight_uom: 'kg', unit_weight_per_qty: '1',
  });
  const [loading, setLoading] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [grades, setGrades] = useState([]);
  const [godowns, setGodowns] = useState([]);
  const [copiedFromName, setCopiedFromName] = useState('');

  // Sheet-metal helpers
  const SHEET_RM_CATEGORIES = ['Full Standard Sheet', 'Cut to Size Strips'];
  // Snap a legacy/case-mismatched sheet-metal category to its canonical label.
  const canonicalCategory = (cat) => {
    if (!cat) return '';
    const match = SHEET_RM_CATEGORIES.find(c => c.toLowerCase().trim() === String(cat).toLowerCase().trim());
    return match || cat;
  };
  const isSheetMetalPartType = form.item_type === 'Sheet Metal Child Part'
    || (form.item_type === 'Finished Good' && form.category === 'sheet-metal-part');
  const isRawSheetMetal = form.item_type === 'Raw Material' && form.sub_category === 'Sheet Metal';
  // Cut-to-Size strips: primary UOM locked to Nos, secondary to Kg, conversion factor = weight/strip
  const isCTS = isRawSheetMetal && form.category === 'Cut to Size Strips';
  const sheetWeight = (() => {
    const t = parseFloat(form.sheet_thickness), l = parseFloat(form.sheet_length), w = parseFloat(form.sheet_width);
    if (t > 0 && l > 0 && w > 0) return (l * w * t * 7.86 / 1000000).toFixed(4);
    return '';
  })();

  useEffect(() => {
    loadSuppliers();
    loadCustomers();
    loadCategories();
    loadGrades();
    inventoryAPI.listGodowns().then(r => setGodowns(r.data || [])).catch(() => {});
    if (isEdit) {
      loadItem();
    } else if (copyFromId) {
      loadItemForCopy();
    }
  }, [id, copyFromId]);

  const loadGrades = async () => {
    try {
      const res = await inventoryAPI.listGrades();
      setGrades(res.data || []);
    } catch { /* ignore */ }
  };

  // Prefill standard sheet dimensions (2500 x 1250) when Full Standard Sheet is chosen
  useEffect(() => {
    if (isRawSheetMetal && form.category === 'Full Standard Sheet') {
      setForm(prev => ({
        ...prev,
        sheet_length: prev.sheet_length || 2500,
        sheet_width: prev.sheet_width || 1250,
      }));
    }
  }, [form.category, form.sub_category, form.item_type]);

  // Cut-to-Size Strips: lock UOM to Nos/Kg and auto-set conversion factor = weight per strip
  useEffect(() => {
    if (!isCTS) return;
    setForm(prev => {
      const next = { ...prev };
      let changed = false;
      if (prev.unit_of_measure !== 'Nos') { next.unit_of_measure = 'Nos'; changed = true; }
      if (prev.secondary_uom !== 'Kg') { next.secondary_uom = 'Kg'; changed = true; }
      const cf = sheetWeight === '' ? '' : String(sheetWeight);
      if (String(prev.conversion_factor ?? '') !== cf) { next.conversion_factor = cf; changed = true; }
      return changed ? next : prev;
    });
  }, [isCTS, sheetWeight]);

  const loadSuppliers = async () => {
    try {
      const res = await suppliersAPI.approvedList();
      setSuppliers(res.data || []);
    } catch { /* ignore - suppliers may not be accessible */ }
  };

  const loadCustomers = async () => {
    try {
      const res = await contractsAPI.activeCustomers();
      setCustomers(res.data || []);
    } catch { /* ignore - customers may not be accessible */ }
  };

  const loadCategories = async () => {
    try {
      const res = await inventoryAPI.listCategories();
      setCategories(res.data?.categories || []);
    } catch { /* ignore */ }
  };

  const loadItem = async () => {
    try {
      const res = await inventoryAPI.getItem(id);
      setForm(prev => ({
        ...prev,
        item_name: res.data.item_name || '',
        item_type: res.data.item_type || 'Raw Material',
        unique_id: res.data.unique_id || '',
        external_id: res.data.external_id || '',
        description: res.data.description || '',
        drawing_number: res.data.drawing_number || '',
        revision: res.data.revision || '',
        unit_of_measure: res.data.unit_of_measure || 'Nos',
        secondary_uom: res.data.secondary_uom || '',
        conversion_factor: res.data.conversion_factor || '',
        is_inventory_item: res.data.is_inventory_item !== false,
        make_or_buy: res.data.make_or_buy || 'buy',
        preferred_supplier_id: res.data.preferred_supplier_id || '',
        preferred_supplier_name: res.data.preferred_supplier_name || '',
        category: canonicalCategory(res.data.category || ''),
        min_stock_level: res.data.min_stock_level || 0,
        reorder_level: res.data.reorder_level || 0,
        hsn_code: res.data.hsn_code || '',
        material_spec: res.data.material_spec || '',
        unit_weight_value: res.data.unit_weight_value ?? '',
        unit_weight_uom: res.data.unit_weight_uom || 'kg',
        unit_weight_per_qty: res.data.unit_weight_per_qty ?? '1',
        sub_category: res.data.sub_category || '',
        sheet_grade: res.data.sheet_grade || '',
        sheet_thickness: res.data.sheet_thickness ?? '',
        sheet_length: res.data.sheet_length ?? '',
        sheet_width: res.data.sheet_width ?? '',
        is_cutting_tool: res.data.is_cutting_tool || false,
        max_regrinds: res.data.max_regrinds || 2,
        customer_id: res.data.customer_id || '',
        customer_name: res.data.customer_name || '',
        auto_indent: res.data.auto_indent || false,
        perform_qc_on_receipt: res.data.perform_qc_on_receipt || false,
        calibration_required: res.data.calibration_required || false,
        auto_notify_low_stock: res.data.auto_notify_low_stock || false,
        default_godown_id: res.data.default_godown_id || '',
      }));
    } catch (err) {
      toast.error('Failed to load item');
      navigate('/inventory/items');
    }
  };

  const loadItemForCopy = async () => {
    try {
      const res = await inventoryAPI.getItem(copyFromId);
      setCopiedFromName(res.data.item_name || '');
      // Copy master data fields but clear unique_id for new entry
      setForm(prev => ({
        ...prev,
        item_name: res.data.item_name || '',
        item_type: res.data.item_type || 'Raw Material',
        unique_id: '', // Clear - user must enter new unique ID
        external_id: res.data.external_id || '',
        description: res.data.description || '',
        drawing_number: res.data.drawing_number || '',
        revision: res.data.revision || '',
        unit_of_measure: res.data.unit_of_measure || 'Nos',
        secondary_uom: res.data.secondary_uom || '',
        conversion_factor: res.data.conversion_factor || '',
        is_inventory_item: res.data.is_inventory_item !== false,
        make_or_buy: res.data.make_or_buy || 'buy',
        preferred_supplier_id: res.data.preferred_supplier_id || '',
        preferred_supplier_name: res.data.preferred_supplier_name || '',
        category: canonicalCategory(res.data.category || ''),
        min_stock_level: res.data.min_stock_level || 0,
        reorder_level: res.data.reorder_level || 0,
        hsn_code: res.data.hsn_code || '',
        material_spec: res.data.material_spec || '',
        unit_weight_value: res.data.unit_weight_value ?? '',
        unit_weight_uom: res.data.unit_weight_uom || 'kg',
        unit_weight_per_qty: res.data.unit_weight_per_qty ?? '1',
        sub_category: res.data.sub_category || '',
        sheet_grade: res.data.sheet_grade || '',
        sheet_thickness: res.data.sheet_thickness ?? '',
        sheet_length: res.data.sheet_length ?? '',
        sheet_width: res.data.sheet_width ?? '',
        is_cutting_tool: res.data.is_cutting_tool || false,
        max_regrinds: res.data.max_regrinds || 2,
        customer_id: res.data.customer_id || '',
        customer_name: res.data.customer_name || '',
        auto_indent: res.data.auto_indent || false,
        perform_qc_on_receipt: res.data.perform_qc_on_receipt || false,
        calibration_required: res.data.calibration_required || false,
        auto_notify_low_stock: res.data.auto_notify_low_stock || false,
      }));
      toast.info(`Creating copy of "${res.data.item_name}". Please enter a new Unique ID.`);
    } catch (err) {
      toast.error('Failed to load source item for copy');
      navigate('/inventory/items');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.item_name || !form.unique_id || !form.item_type) {
      toast.error('Item name, unique ID, and type are required');
      return;
    }
    if (form.secondary_uom && (!form.conversion_factor || parseFloat(form.conversion_factor) <= 0)) {
      toast.error('Conversion factor is required when secondary UOM is defined');
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (payload.conversion_factor) payload.conversion_factor = parseFloat(payload.conversion_factor);
      if (!payload.secondary_uom) { payload.secondary_uom = ''; payload.conversion_factor = null; }
      // Optional shipping weight
      payload.unit_weight_value = form.unit_weight_value === '' || form.unit_weight_value == null ? null : parseFloat(form.unit_weight_value);
      payload.unit_weight_per_qty = form.unit_weight_per_qty === '' || form.unit_weight_per_qty == null ? null : parseFloat(form.unit_weight_per_qty);
      payload.unit_weight_uom = payload.unit_weight_value != null ? (form.unit_weight_uom || 'kg') : '';
      // Sheet-metal raw material: cast numeric dims (or clear when not applicable)
      if (isRawSheetMetal) {
        payload.sheet_thickness = form.sheet_thickness ? parseFloat(form.sheet_thickness) : null;
        payload.sheet_length = form.sheet_length ? parseFloat(form.sheet_length) : null;
        payload.sheet_width = form.sheet_width ? parseFloat(form.sheet_width) : null;
        if (!payload.sheet_thickness || !payload.sheet_length || !payload.sheet_width) {
          toast.error('Thickness, Length and Width are required for a sheet-metal raw material');
          setLoading(false);
          return;
        }
        if (!payload.sheet_grade) { toast.error('Grade is required for sheet-metal raw material'); setLoading(false); return; }
        if (!form.category) { toast.error('Select Full Standard Sheet or Cut to Size Strips'); setLoading(false); return; }
        // Cut-to-Size Strips: lock UOM to Nos/Kg with conversion factor = weight per strip
        if (isCTS) {
          payload.unit_of_measure = 'Nos';
          payload.secondary_uom = 'Kg';
          const w = parseFloat(sheetWeight);
          payload.conversion_factor = w > 0 ? w : null;
          if (!payload.conversion_factor) {
            toast.error('Enter valid dimensions so the strip weight (conversion factor) can be computed');
            setLoading(false);
            return;
          }
        }
      } else {
        payload.sub_category = ''; payload.sheet_grade = ''; payload.sheet_thickness = null;
        payload.sheet_length = null; payload.sheet_width = null;
      }
      if (isEdit) {
        const res = await inventoryAPI.updateItem(id, payload);
        if (res.data?.pending_approval) {
          toast.warning(res.data.message || 'Changes sent for admin approval', { duration: 9000 });
        } else {
          toast.success('Item updated');
        }
        navigate(`/inventory/items/${id}`);
      } else {
        const res = await inventoryAPI.createItem(payload);
        toast.success(res.data?.status === 'pending_approval'
          ? 'Item created — awaiting admin approval'
          : 'Item created');
        navigate(`/inventory/items/${res.data.id}`);
      }
    } catch (err) {
      showApiError(err, 'Failed to save item');
    } finally { setLoading(false); }
  };

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  return (
    <div className="space-y-4 max-w-4xl" data-testid="item-form-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="font-heading text-xl font-bold tracking-tight">
            {isEdit ? 'Edit Item' : copyFromId ? 'Copy Item' : 'New Item'}
          </h1>
          {copyFromId && copiedFromName && (
            <p className="text-xs text-muted-foreground">Creating copy from: {copiedFromName}</p>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Basic Info */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-sm font-medium">Basic Information</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Unique ID *</Label>
              <Input value={form.unique_id} onChange={e => updateField('unique_id', e.target.value)}
                disabled={isEdit} placeholder="e.g. RM-AL6061-001" className="h-8 text-xs font-mono" data-testid="item-unique-id" />
            </div>
            <div>
              <Label className="text-xs">Item Name *</Label>
              <Input value={form.item_name} onChange={e => updateField('item_name', e.target.value)}
                placeholder="e.g. AL 6061 Round Bar" className="h-8 text-xs" data-testid="item-name" />
            </div>
            <div>
              <Label className="text-xs">Item Type *</Label>
              <Select value={form.item_type} onValueChange={v => { if (v) updateField('item_type', v); }}>
                <SelectTrigger className="h-8 text-xs" data-testid="item-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from(new Set([...ITEM_TYPES, ...(form.item_type ? [form.item_type] : [])]))
                    .map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">External Id (Name Given to this Item By External Supplier)</Label>
              <Input value={form.external_id} onChange={e => updateField('external_id', e.target.value)}
                placeholder="Supplier's part name" className="h-8 text-xs" data-testid="item-external-id" />
            </div>
            {form.item_type === 'Raw Material' && (
              <div>
                <Label className="text-xs">Sub-Category</Label>
                <Select value={form.sub_category || '_none'} onValueChange={v => { if (!v) return; updateField('sub_category', v === '_none' ? '' : v); updateField('category', ''); }}>
                  <SelectTrigger className="h-8 text-xs" data-testid="item-subcategory">
                    <SelectValue placeholder="Select sub-category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">-- None --</SelectItem>
                    <SelectItem value="Sheet Metal">Sheet Metal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-xs">Category</Label>
              <Select value={form.category || '_none'} onValueChange={v => { if (!v) return; updateField('category', v === '_none' ? '' : v); }}>
                <SelectTrigger className="h-8 text-xs" data-testid="item-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {isRawSheetMetal ? (
                    // Always include the stored value so a legacy/mismatched category never renders blank.
                    Array.from(new Set([...SHEET_RM_CATEGORIES, ...(form.category ? [form.category] : [])]))
                      .map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)
                  ) : (
                    <>
                      <SelectItem value="_none">-- No Category --</SelectItem>
                      {(() => {
                        // Build a de-duplicated option list. The stored value is ALWAYS
                        // included so a legacy/free-text category never renders blank or
                        // gets silently lost on save.
                        const opts = [];
                        if (form.item_type === 'Finished Good') opts.push('sheet-metal-part', 'Welded Assembly');
                        else if (form.item_type === 'Sheet Metal Child Part') opts.push('sheet-metal-part');
                        categories.forEach(c => { if (c.name && !opts.includes(c.name)) opts.push(c.name); });
                        if (form.category && !opts.includes(form.category)) opts.push(form.category);
                        return opts.map(name => <SelectItem key={name} value={name}>{name}</SelectItem>);
                      })()}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
            {isRawSheetMetal && form.category && (
              <div className="col-span-2 grid grid-cols-4 gap-3 p-3 rounded-md border border-amber-300 bg-amber-50/60" data-testid="sheet-metal-fields">
                <div>
                  <Label className="text-xs">Grade *</Label>
                  <Select value={form.sheet_grade || '_none'} onValueChange={v => updateField('sheet_grade', v === '_none' ? '' : v)}>
                    <SelectTrigger className="h-8 text-xs" data-testid="sheet-grade"><SelectValue placeholder="Grade" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">-- Select --</SelectItem>
                      {grades.map(g => <SelectItem key={g.id} value={g.name}>{g.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Thickness (mm) *</Label>
                  <Input type="number" step="0.01" className="h-8 text-xs" value={form.sheet_thickness}
                    onChange={e => updateField('sheet_thickness', e.target.value)} placeholder="e.g. 1.2" data-testid="sheet-thickness" />
                </div>
                <div>
                  <Label className="text-xs">Length (mm) *</Label>
                  <Input type="number" step="any" className="h-8 text-xs" value={form.sheet_length}
                    onChange={e => updateField('sheet_length', e.target.value)}
                    placeholder={form.category === 'Full Standard Sheet' ? '2500' : ''} data-testid="sheet-length" />
                </div>
                <div>
                  <Label className="text-xs">Width (mm) *</Label>
                  <Input type="number" step="any" className="h-8 text-xs" value={form.sheet_width}
                    onChange={e => updateField('sheet_width', e.target.value)}
                    placeholder={form.category === 'Full Standard Sheet' ? '1250' : ''} data-testid="sheet-width" />
                </div>
                <div className="col-span-4">
                  <Label className="text-xs">Weight of Sheet / Strip (Kg)</Label>
                  <Input readOnly value={sheetWeight} className="h-8 text-xs font-mono bg-white" placeholder="auto-calculated" data-testid="sheet-weight" />
                  <p className="text-[10px] text-muted-foreground mt-0.5">= L × W × T × 7.86 / 1,000,000 (steel density 7.86)</p>
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs">Primary UOM {isCTS && <span className="text-amber-600">(locked: Nos)</span>}</Label>
              <Select value={form.unit_of_measure} onValueChange={v => updateField('unit_of_measure', v)} disabled={isCTS}>
                <SelectTrigger className="h-8 text-xs" data-testid="item-uom">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {UOMS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Secondary UOM {isCTS ? <span className="text-amber-600">(locked: Kg)</span> : <span className="text-muted-foreground">(optional)</span>}</Label>
              <Select value={form.secondary_uom || 'none'} disabled={isCTS} onValueChange={v => {
                updateField('secondary_uom', v === 'none' ? '' : v);
                if (v === 'none') updateField('conversion_factor', '');
              }}>
                <SelectTrigger className="h-8 text-xs" data-testid="item-secondary-uom">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {UOMS.filter(u => u !== form.unit_of_measure).map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {form.secondary_uom && (
              <div>
                <Label className="text-xs">Conversion Factor * {isCTS && <span className="text-amber-600">(auto: weight/strip)</span>}</Label>
                <Input type="number" step="any" className={`h-8 text-xs ${isCTS ? 'bg-muted cursor-not-allowed' : ''}`} value={form.conversion_factor}
                  readOnly={isCTS}
                  onChange={e => { if (!isCTS) updateField('conversion_factor', e.target.value); }}
                  placeholder={`1 ${form.unit_of_measure} = ? ${form.secondary_uom}`}
                  data-testid="item-conversion-factor" />
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  {isCTS
                    ? `Auto-copied from weight per strip: 1 Nos = ${form.conversion_factor || '?'} Kg`
                    : `1 ${form.unit_of_measure} = ${form.conversion_factor || '?'} ${form.secondary_uom}`}
                </p>
              </div>
            )}
            <div className="md:col-span-2">
              <Label className="text-xs">Description</Label>
              <Textarea value={form.description} onChange={e => updateField('description', e.target.value)}
                placeholder="Item description..." className="text-xs min-h-[60px]" data-testid="item-description" />
            </div>
          </CardContent>
        </Card>

        {/* Technical Details */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-sm font-medium">Technical Details</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Drawing Number</Label>
              <Input value={form.drawing_number} onChange={e => updateField('drawing_number', e.target.value)}
                className="h-8 text-xs font-mono" data-testid="item-drawing" />
            </div>
            <div>
              <Label className="text-xs">Revision</Label>
              <Input value={form.revision} onChange={e => updateField('revision', e.target.value)}
                className="h-8 text-xs" data-testid="item-revision" />
            </div>
            <div>
              <Label className="text-xs">Material Specification</Label>
              <Input value={form.material_spec} onChange={e => updateField('material_spec', e.target.value)}
                placeholder="e.g. AL 6061 T6" className="h-8 text-xs" data-testid="item-material-spec" />
            </div>
            <div>
              <Label className="text-xs">HSN Code</Label>
              <Input value={form.hsn_code} onChange={e => updateField('hsn_code', e.target.value)}
                className="h-8 text-xs font-mono" data-testid="item-hsn" />
            </div>
            <div className="md:col-span-2 lg:col-span-3 border rounded-md p-2.5 bg-muted/20">
              <Label className="text-xs font-medium">Shipping Weight (optional) — used for delivery-challan shipment weight</Label>
              <div className="grid grid-cols-3 gap-2 mt-1.5">
                <div>
                  <Label className="text-[10px] text-muted-foreground">Weight</Label>
                  <Input type="number" step="any" min="0" value={form.unit_weight_value}
                    onChange={e => updateField('unit_weight_value', e.target.value)}
                    placeholder="e.g. 2.5" className="h-8 text-xs" data-testid="item-weight-value" />
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">Unit</Label>
                  <Select value={form.unit_weight_uom} onValueChange={v => v && updateField('unit_weight_uom', v)}>
                    <SelectTrigger className="h-8 text-xs" data-testid="item-weight-uom"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="kg">kg</SelectItem>
                      <SelectItem value="gram">gram</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">Per quantity (Nos)</Label>
                  <Input type="number" step="any" min="1" value={form.unit_weight_per_qty}
                    onChange={e => updateField('unit_weight_per_qty', e.target.value)}
                    placeholder="e.g. 1, 10, 100" className="h-8 text-xs" data-testid="item-weight-per-qty" />
                </div>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">e.g. "2.5 kg per 10 Nos" means each piece weighs 0.25 kg. Leave blank if not tracked.</p>
            </div>
            <div>
              <Label className="text-xs">Customer</Label>
              <SearchableSelect
                options={[{ value: '', label: 'None' }, ...customers.map(c => ({ value: c.id, label: c.customer_name, sub: c.gst_no || '', searchStr: `${c.customer_name} ${c.gst_no || ''}` }))]}
                value={form.customer_id || ''}
                onSelect={v => {
                  const cust = customers.find(c => c.id === v);
                  updateField('customer_id', v || '');
                  updateField('customer_name', cust ? cust.customer_name : '');
                }}
                placeholder="Search customers..."
                emptyText="No customers found."
                testId="item-customer"
              />
            </div>
          </CardContent>
        </Card>

        {/* Inventory Settings */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-sm font-medium">Inventory & Sourcing</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-xs font-medium">Inventory Item</Label>
                <p className="text-[10px] text-muted-foreground">Track quantity (receipt/issue)</p>
              </div>
              <Switch checked={form.is_inventory_item} onCheckedChange={v => updateField('is_inventory_item', v)} data-testid="is-inventory-switch" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Sourcing</Label>
                <Select value={form.make_or_buy} onValueChange={v => updateField('make_or_buy', v)}>
                  <SelectTrigger className="h-8 text-xs" data-testid="make-buy-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SOURCING_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {form.make_or_buy === 'buy' && (
                <div>
                  <Label className="text-xs">Preferred Supplier</Label>
                  <SearchableSelect
                    options={[{ value: '', label: 'None' }, ...suppliers.map(s => ({ value: s.id, label: s.supplier_name, searchStr: `${s.supplier_name} ${s.supplier_code || ''}` }))]}
                    value={form.preferred_supplier_id || ''}
                    onSelect={v => {
                      const sup = suppliers.find(s => s.id === v);
                      updateField('preferred_supplier_id', v || '');
                      updateField('preferred_supplier_name', sup ? sup.supplier_name : '');
                    }}
                    placeholder="Search suppliers..."
                    emptyText="No approved suppliers."
                    testId="supplier-select"
                  />
                </div>
              )}
              {form.make_or_buy === 'receive_from_customer' && (
                <div>
                  <Label className="text-xs">Customer (Source)</Label>
                  <SearchableSelect
                    options={[{ value: '', label: 'None' }, ...customers.map(c => ({ value: c.id, label: c.customer_name, searchStr: `${c.customer_name} ${c.gst_no || ''}` }))]}
                    value={form.customer_id || ''}
                    onSelect={v => {
                      const cust = customers.find(c => c.id === v);
                      updateField('customer_id', v || '');
                      updateField('customer_name', cust ? cust.customer_name : '');
                    }}
                    placeholder="Search customers..."
                    emptyText="No customers found."
                    testId="item-source-customer"
                  />
                  <label className="flex items-center gap-2 mt-2 text-[11px] cursor-pointer select-none" data-testid="auto-notify-low-stock-label">
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 accent-blue-600"
                      checked={!!form.auto_notify_low_stock}
                      onChange={e => updateField('auto_notify_low_stock', e.target.checked)}
                      data-testid="auto-notify-low-stock"
                    />
                    Auto-notify customer of low stock <span className="text-muted-foreground">(weekly email when below reorder level)</span>
                  </label>
                </div>
              )}
            </div>
            {form.is_inventory_item && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Min Stock Level</Label>
                  <Input type="number" value={form.min_stock_level} onChange={e => updateField('min_stock_level', parseFloat(e.target.value) || 0)}
                    className="h-8 text-xs" data-testid="min-stock" />
                </div>
                <div>
                  <Label className="text-xs">Reorder Level</Label>
                  <Input type="number" value={form.reorder_level} onChange={e => updateField('reorder_level', parseFloat(e.target.value) || 0)}
                    className="h-8 text-xs" data-testid="reorder-level" />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Cutting Tool Settings */}
        {form.item_type === 'Consumable' && (
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-sm font-medium">Cutting Tool Settings</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-xs font-medium">Is Cutting Tool</Label>
                  <p className="text-[10px] text-muted-foreground">Enable regrind tracking with auto-scrap</p>
                </div>
                <Switch checked={form.is_cutting_tool} onCheckedChange={v => updateField('is_cutting_tool', v)} data-testid="is-cutting-tool-switch" />
              </div>
              {form.is_cutting_tool && (
                <div>
                  <Label className="text-xs">Max Regrinds Before Scrap</Label>
                  <Input type="number" value={form.max_regrinds} onChange={e => updateField('max_regrinds', parseInt(e.target.value) || 2)}
                    min={1} max={10} className="h-8 text-xs w-32" data-testid="max-regrinds" />
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Purchase & Quality Settings */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Purchase & Quality Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-xs font-medium">Auto Indent</Label>
                <p className="text-[10px] text-muted-foreground">Automatically add to purchase indent when stock falls below minimum</p>
              </div>
              <Switch checked={form.auto_indent} onCheckedChange={v => updateField('auto_indent', v)} data-testid="auto-indent-switch" />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-xs font-medium">Perform QC on Receipt</Label>
                <p className="text-[10px] text-muted-foreground">Incoming receipt inspection (OK/Quarantine/Reject)</p>
              </div>
              <Switch checked={form.perform_qc_on_receipt} onCheckedChange={v => updateField('perform_qc_on_receipt', v)} data-testid="perform-qc-switch" />
            </div>
            <div>
              <Label className="text-xs font-medium">Default godown on receipt</Label>
              {form.perform_qc_on_receipt ? (
                <>
                  <div className="h-9 text-xs flex items-center px-3 border rounded-md bg-amber-50 border-amber-200 text-amber-900 font-medium mt-1"
                    data-testid="default-godown-locked">
                    Inbound Staging (fixed — QC on receipt is ON)
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    QC-checked material must land in Inbound Staging. Turn off "Perform QC on Receipt" to choose another location.
                  </p>
                </>
              ) : (
                <>
                  <Select value={form.default_godown_id || ''} onValueChange={v => updateField('default_godown_id', v)}>
                    <SelectTrigger className="h-9 text-xs mt-1" data-testid="default-godown-select">
                      <SelectValue placeholder="Stores (default)" />
                    </SelectTrigger>
                    <SelectContent>
                      {godowns.map(g => (
                        <SelectItem key={g.id} value={g.id} className="text-xs">{g.code} - {g.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    Auto-selected when a receipt is entered for this item. Leave empty to use Stores.
                  </p>
                </>
              )}
            </div>
            {form.item_type === 'Fixed Asset' && (
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-xs font-medium">Calibration Required</Label>
                  <p className="text-[10px] text-muted-foreground">Track this asset in calibration app</p>
                </div>
                <Switch checked={form.calibration_required} onCheckedChange={v => updateField('calibration_required', v)} data-testid="calibration-required-switch" />
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
          <Button type="submit" disabled={loading} data-testid="save-item-btn">
            <Save className="w-4 h-4 mr-1" />
            {loading ? 'Saving...' : isEdit ? 'Update Item' : 'Create Item'}
          </Button>
        </div>
      </form>
    </div>
  );
}
